import { CommonModule } from '@angular/common';
import { Component, inject, input, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PatronAddressModel, TransactionWrapper } from '@core/models';
import { ConfigurationService } from '@core/services';
import { TransactionService } from '@core/services/transaction.service';
import { LegalTermsComponent } from '@features/pages/checkout/components/legal-terms/legal-terms.component';
import { PaymentDetails } from '../../models/payment-details.model';

@Component({
  selector: 'app-checkout-recap',
  standalone: true,
  imports: [CommonModule, FormsModule, LegalTermsComponent],
  templateUrl: './checkout-recap.component.html',
  styleUrl: './checkout-recap.component.css',
})
export class CheckoutRecapComponent {
  configurationService = inject(ConfigurationService);
  transactionService = inject(TransactionService);

  public isTicketFeeEnabled = input.required<boolean>();
  public isDiscountCodesEnabled = input.required<boolean>();
  /**
   * Which half of this component's template to render:
   * the checkout page renders the price recap and the legal-terms
   * checkbox in two different spots within the same "Payment Options" card
   * — recap near the top, terms/checkbox below the card-details form —
   * instead of the two side by side. Same data/bindings either way, just
   * split rendering so the parent can place each half where its layout
   * puts it.
   */
  public section = input<'totals' | 'legal'>('totals');
  @Input() transaction: TransactionWrapper;
  @Input() paymentDetails: Omit<
    PaymentDetails,
    | 'paymentMethod'
    | 'cardType'
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'address'
  > & {
    paymentMethod: string | null;
    cardType: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    address:
      | (Omit<PatronAddressModel, 'city' | 'postalCode' | 'address1'> & {
          city: string | null;
          postalCode: string | null;
          address1: string | null;
        })
      | null;
    phone: {
      phone: string;
      countryCode: string;
    } | null;
  } = {
    nonce: null,
    paymentMethod: null,
    cardType: null,
    acceptedTerms: false,
    firstName: null,
    lastName: null,
    email: null,
    digitalWallet: null,
    accountCredit: {
      credits: 0,
      payFull: false,
    },
    address: null,
    phone: null,
  };

  get serviceChargesDetails() {
    return this.transactionService.getServiceChargesDetails(
      this.transaction.transaction.tdcTransaction!,
      this.transaction?.client
    );
  }

  get hasActiveDiscount(): boolean {
    const tdc = this.transaction?.transaction?.tdcTransaction;
    return this.transactionService.hasActiveDiscount(tdc);
  }

  get discountCode(): string | null {
    return this.transactionService.getDiscountCode(
      this.transaction?.transaction
    );
  }

  get totalDiscount(): number | null {
    const tdc = this.transaction?.transaction?.tdcTransaction;
    return this.transactionService.getTotalDiscount(tdc);
  }

  get displayTotalPriceSeats(): number {
    const tdc = this.transaction?.transaction?.tdcTransaction;
    const transactionUpsell = this.transactionService.getTransactionUpsell();
    const upsellPrice = transactionUpsell?.price ?? 0;
    const totalSeats = this.transactionService.getTotalSeats();

    return this.transactionService.getTotalPriceSeatsForDisplay(
      tdc,
      upsellPrice,
      totalSeats
    );
  }
}
